import React, { useCallback, useEffect, useState } from "react";
import { collection, addDoc, doc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { db } from "./firebase";
import { EmptyState, PageHeader } from "./petaarUi";

export default function MyPets({ user, selectedPetId, onSelectPet }) {
  const [pets, setPets] = useState([]);
  const [name, setName] = useState("");
  const [species, setSpecies] = useState("dog");
  const [breed, setBreed] = useState("");
  const [age, setAge] = useState("");
  const [weight, setWeight] = useState("");
  const [healthDrafts, setHealthDrafts] = useState({});

  const loadPets = useCallback(async () => {
    if (!user?.uid) return;
    const q = query(collection(db, "pets"), where("userId", "==", user.uid));
    const snap = await getDocs(q);
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setPets(list);
    setHealthDrafts(
      list.reduce((acc, pet) => ({
        ...acc,
        [pet.id]: {
          allergies: pet.allergies || "",
          medications: pet.medications || "",
          vaccinationStatus: pet.vaccinationStatus || "",
          lastVetVisit: pet.lastVetVisit || "",
          notes: pet.notes || "",
        },
      }), {}),
    );
    if (!selectedPetId && list.length > 0) {
      onSelectPet(list[0]);
    }
  }, [user?.uid, selectedPetId, onSelectPet]);

  useEffect(() => {
    loadPets();
  }, [loadPets]);

  const addPet = async () => {
    if (!name || !breed || !age || !weight) {
      alert("Please fill pet details");
      return;
    }
    const ageNum = Number(age);
    const weightNum = Number(weight);
    if (!Number.isFinite(ageNum) || !Number.isFinite(weightNum)) {
      alert("Age and weight must be numbers");
      return;
    }

    await addDoc(collection(db, "pets"), {
      userId: user.uid,
      name: name.trim(),
      species,
      breed: breed.trim(),
      age: ageNum,
      weight: weightNum,
      createdAt: new Date(),
    });

    setName("");
    setSpecies("dog");
    setBreed("");
    setAge("");
    setWeight("");
    await loadPets();
  };

  const saveHealthProfile = async (petId) => {
    const draft = healthDrafts[petId] || {};
    await updateDoc(doc(db, "pets", petId), {
      allergies: String(draft.allergies || "").trim(),
      medications: String(draft.medications || "").trim(),
      vaccinationStatus: String(draft.vaccinationStatus || "").trim(),
      lastVetVisit: String(draft.lastVetVisit || "").trim(),
      notes: String(draft.notes || "").trim(),
    });
    await loadPets();
    alert("Pet health profile saved.");
  };

  return (
    <div className="petaar-page">
      <PageHeader
        eyebrow="My Pets"
        title="Your pet profiles"
        subtitle="Manage health basics, vaccination notes, and which pet should be used for AI or consultation flows."
      />

      <div className="petaar-card" style={{ marginBottom: 24 }}>
        <div className="petaar-section-title">
          <div>
            <div className="petaar-kicker">Add new pet</div>
            <h2 style={{ marginTop: 4 }}>Create a profile</h2>
          </div>
        </div>
        <div className="petaar-form-grid">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Pet name" />
          <select value={species} onChange={(e) => setSpecies(e.target.value)}>
            <option value="dog">Dog</option>
            <option value="cat">Cat</option>
            <option value="other">Other</option>
          </select>
          <input value={breed} onChange={(e) => setBreed(e.target.value)} placeholder="Breed" />
          <input value={age} onChange={(e) => setAge(e.target.value)} placeholder="Age (months)" />
          <input value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="Weight (kg)" />
          <button className="petaar-button" onClick={addPet}>Add Pet</button>
        </div>
      </div>

      {pets.length === 0 ? (
        <EmptyState
          icon="🐕"
          title="No pets added yet"
          description="Add your first pet profile to speed up bookings, consultations, and AI recommendations."
        />
      ) : (
        <div className="petaar-grid two">
          {pets.map((pet) => (
            <div
              key={pet.id}
              className="petaar-card"
              style={{
                background: pet.id === selectedPetId ? "linear-gradient(180deg, #ffffff 0%, #f3f6ff 100%)" : "#fff",
                borderColor: pet.id === selectedPetId ? "rgba(26, 26, 219, 0.18)" : undefined,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div className="petaar-stat-icon" style={{ width: 58, height: 58, fontSize: "1.5rem" }}>
                    {pet.species === "cat" ? "🐱" : pet.species === "dog" ? "🐶" : "🐾"}
                  </div>
                  <div>
                    <strong style={{ display: "block", fontSize: "1.1rem" }}>{pet.name}</strong>
                    <small style={{ color: "var(--muted)" }}>{pet.breed || "Breed not added"} • {pet.age} months</small>
                  </div>
                </div>
                <button className={pet.id === selectedPetId ? "petaar-button" : "petaar-button--secondary"} onClick={() => onSelectPet(pet)}>
                  {pet.id === selectedPetId ? "Selected" : "Use for AI"}
                </button>
              </div>

              <div className="petaar-chip-row" style={{ marginBottom: 18 }}>
                <span className="petaar-chip">{pet.species}</span>
                <span className="petaar-chip">{pet.weight} kg</span>
              </div>

              <div className="petaar-surface" style={{ padding: 18 }}>
                <strong style={{ display: "block", marginBottom: 12 }}>Health profile</strong>
                <div className="petaar-form-grid">
                  <input
                    placeholder="Allergies"
                    value={healthDrafts[pet.id]?.allergies || ""}
                    onChange={(e) => setHealthDrafts((prev) => ({
                      ...prev,
                      [pet.id]: { ...(prev[pet.id] || {}), allergies: e.target.value },
                    }))}
                  />
                  <input
                    placeholder="Current medications"
                    value={healthDrafts[pet.id]?.medications || ""}
                    onChange={(e) => setHealthDrafts((prev) => ({
                      ...prev,
                      [pet.id]: { ...(prev[pet.id] || {}), medications: e.target.value },
                    }))}
                  />
                  <input
                    placeholder="Vaccination status"
                    value={healthDrafts[pet.id]?.vaccinationStatus || ""}
                    onChange={(e) => setHealthDrafts((prev) => ({
                      ...prev,
                      [pet.id]: { ...(prev[pet.id] || {}), vaccinationStatus: e.target.value },
                    }))}
                  />
                  <input
                    type="date"
                    value={healthDrafts[pet.id]?.lastVetVisit || ""}
                    onChange={(e) => setHealthDrafts((prev) => ({
                      ...prev,
                      [pet.id]: { ...(prev[pet.id] || {}), lastVetVisit: e.target.value },
                    }))}
                  />
                </div>
                <textarea
                  placeholder="Notes (symptoms history, vet comments, etc.)"
                  value={healthDrafts[pet.id]?.notes || ""}
                  onChange={(e) => setHealthDrafts((prev) => ({
                    ...prev,
                    [pet.id]: { ...(prev[pet.id] || {}), notes: e.target.value },
                  }))}
                  style={{ marginTop: 14 }}
                />
                <div style={{ marginTop: 14 }}>
                  <button className="petaar-button" onClick={() => saveHealthProfile(pet.id)}>
                    Save Health Profile
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
